import { FC, createContext, useContext, useState, useMemo } from 'react';
import { compact } from 'lodash';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useRequest } from '~hooks/useRequest';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { getVehicles } from '~services/vehicle';
import { VehicleBase } from '~services/vehicle/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { DriverCompanyBase } from '~services/driver/types';
import { getServiceTypesCompany } from '~services/enumerations';
import { getLogistiscZonesInfo } from '~services/logisticsZones';
import { LogisticZoneBase } from '~services/logisticsZones/types';
import { getUsersWithDrivers } from '~services/route';
import { RouteUserBasic } from '~services/route/types';
import { AuthExtendedCompany } from '~services/auth/types';
import { getGiverCompanies } from '~services/partnerCompany';
import { SelectOption } from '~globals/types/commons';
import { DEFAULT_UUIID_EMPTY } from '~constants/commons';
import { sortAlphabeticZoneByCode } from '~utils/logisiticsZones';
import { getFeatureAvailableToDate } from '~utils/feature';

import { DashboardVariantV2CarrierFiltersContextSelectsData } from './types';

export const DEFAULT_DRIVER_OPTION: DriverCompanyBase = {
  code: null,
  displayName: 'Sin asignar',
  enabled: true,
  id: DEFAULT_UUIID_EMPTY,
  avatar: null,
};

export const DEFAULT_SERVICE_TYPE_OPTION: SelectOption = {
  value: '_unassigned',
  label: 'Sin asignar',
};

export const DEFAULT_ZONE_OPTION: LogisticZoneBase = {
  code: 'Sin asignar',
  color: '',
  description: '',
  zoneId: DEFAULT_UUIID_EMPTY,
};

const DashboardVariantV2CarrierSelectDataContext =
  createContext<DashboardVariantV2CarrierFiltersContextSelectsData>({
    vehicles: {
      loading: true,
      list: [],
    },
    drivers: {
      loading: true,
      list: [],
    },
    services: {
      loading: true,
      list: [],
    },
    logisticsZones: {
      loading: true,
      list: [],
    },
    assignedUser: {
      loading: true,
      list: [],
    },
    giverCompany: {
      loading: true,
      list: [],
    },
  });

export const useDashboardVariantV2CarrierSelectDataContext =
  (): DashboardVariantV2CarrierFiltersContextSelectsData =>
    useContext(DashboardVariantV2CarrierSelectDataContext);

const DashboardVariantV2CarrierSelectDataProvider: FC = ({ children }) => {
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [vehicles, setVehicles] = useState<VehicleBase[]>([]);
  const [, loadingVehicles] = useRequest({
    request: getVehicles,
    payload: null,
    withPostSuccess: (response) => {
      const vehiclesResponse = response.data?.data as VehicleBase[];

      setVehicles(vehiclesResponse);
    },
  });

  const [drivers, setDrivers] = useState<DriverCompanyBase[]>([]);
  const [, loadingDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const availablesDriversResponse = response.data?.data
        .results as DriverCompanyBase[];

      setDrivers([DEFAULT_DRIVER_OPTION, ...availablesDriversResponse]);
    },
  });

  const [serviceTypes, setServicesTypes] = useState<SelectOption[]>([]);
  const [, loadingServiceTypes] = useRequest({
    request: getServiceTypesCompany,
    payload: null,
    withPostSuccess: (response) => {
      const servicesListResponse = compact(
        response.data?.data.results as string[],
      );

      const currentServiceTypes: SelectOption[] = servicesListResponse.map(
        (service) => ({
          value: service,
          label: service,
        }),
      );

      setServicesTypes([DEFAULT_SERVICE_TYPE_OPTION, ...currentServiceTypes]);
    },
  });

  const [logisticsZones, setLogisticsZone] = useState<LogisticZoneBase[]>([]);
  const [, loadingLogisticsZone] = useRequest({
    request: getLogistiscZonesInfo,
    payload: null,
    withPostSuccess: (response) => {
      const logisitcisZoneResponse = response.data?.data
        .results as LogisticZoneBase[];

      const sortedLogisitcisZoneResponse = sortAlphabeticZoneByCode(
        logisitcisZoneResponse,
      );

      setLogisticsZone([DEFAULT_ZONE_OPTION, ...sortedLogisitcisZoneResponse]);
    },
  });

  const [assignedUsers, setAssignedUsers] = useState<RouteUserBasic[]>([]);
  const [, loadingAssignedUsers] = useRequest({
    request: getUsersWithDrivers,
    payload: null,
    withPostSuccess: (response) => {
      const currentUsersResponse = response.data?.data
        .results as RouteUserBasic[];

      setAssignedUsers(currentUsersResponse);
    },
  });

  const isEnabledGiversModule = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmGiversModule,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmGiversModule),
      ),
    [hasAccessToFeature],
  );
  const [giverCompanies, setGiverCompanies] = useState<AuthExtendedCompany[]>(
    [],
  );
  const [, loadingGiverCompanies] = useRequest({
    request: getGiverCompanies,
    payload: null,
    isValidToRequest: isEnabledGiversModule,
    initialLoading: isEnabledGiversModule,
    withPostSuccess: (response) => {
      const giverCompaniesResponse = response.data?.data
        .results as AuthExtendedCompany[];

      setGiverCompanies(giverCompaniesResponse);
    },
  });

  return (
    <DashboardVariantV2CarrierSelectDataContext.Provider
      value={{
        vehicles: {
          loading: loadingVehicles,
          list: vehicles,
        },
        drivers: {
          loading: loadingDrivers,
          list: drivers,
        },
        services: {
          loading: loadingServiceTypes,
          list: serviceTypes,
        },
        logisticsZones: {
          loading: loadingLogisticsZone,
          list: logisticsZones,
        },
        assignedUser: {
          loading: loadingAssignedUsers,
          list: assignedUsers,
        },
        giverCompany: {
          loading: loadingGiverCompanies,
          list: giverCompanies,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierSelectDataContext.Provider>
  );
};

export default DashboardVariantV2CarrierSelectDataProvider;
